import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, formSetValue, useCpFormAnyValueId, useCpFormModifiedAnyId, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { homeLink, linksOf, request } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { PolicyInput } from "../../schema/types/policy/input";
import { PermissionWithImplied, Policy } from "../../schema/types/policy/entity";
import { Permissions } from "../../schema/types/permissions";
import { PolicyDetailHeader } from "./header";
import { Summary } from "./summary";
import { PolicyTarget } from "./target";
import { TargetItems } from "./targetItems/targetItems";
import { Bindings } from "./bindings";
import { TargetQuery } from "./targetQuery/targetQuery";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  policy: Policy;
  refetch?: () => void;
}
export const PolicyDetailContent: React.FC<Props> = ({ policy, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/policy/:policy/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(policy).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(policy.lastModified);
  }, [policy.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);
  const formVersion = useCpFormValue(`version`);

  const targetKind = useCpFormAnyValueId(formId, `values.targetKind`);

  React.useEffect(() => {
    fetchPermissions(targetKind);
  }, [targetKind]);

  async function fetchPermissions(targetKind: string) {
    if (!targetKind) {
      return;
    }

    const form = store.getState().form.instances[formId];
    const values: PolicyInput = form.values as any;

    const permissions = values.meta.permissionsWithImplied[targetKind];
    if (permissions) {
      return;
    }
    try {
      const { data } = await request<Permissions>({ url: `${homeLink(targetKind as any)}/-schema/permissions` });
      const permissionsWithImplied: PermissionWithImplied[] = [];
      for (const _permission of data.items) {
        const permission: PermissionWithImplied = {
          ..._permission,
          description: _permission.description || "",
          implied: data.implied?.[_permission.name] || [],
        };
        permissionsWithImplied.push(permission);
      }
      formSetValue(formId, `meta.permissionsWithImplied.${targetKind}`, permissionsWithImplied);
    } catch (e) {
      // TODO show error page here
    }
  }

  React.useEffect(() => {
    if (!formVersion) return;
    fetchTargets();
  }, [formVersion]);

  async function fetchTargets() {
    const form = store.getState().form.instances[formId];
    const values: PolicyInput = form.values;

    const targets = cloneDeep(values.targets);

    for (const target of targets) {
      const link = target.link;
      const { data } = await request({ url: link });
      target.item = data;
    }
    formSetValue(formId, `targets`, targets);
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Target", "/target", { names: ["targetAll"], showDirty: true }),
      toLinkPage("Items", "/items", { names: ["targets"], showDirty: true }),
      toLinkPage("Query", "/query", { names: ["targetQuery"], showDirty: true }),
      toLinkPage("Bindings", "/bindings", { names: ["bindings"], showDirty: true }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <PolicyDetailHeader policy={policy} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary policy={policy} />} />
              <Route path={`target/*`} element={<PolicyTarget policy={policy} />} />
              <Route path={`items/*`} element={<TargetItems />} />
              <Route path={`query/*`} element={<TargetQuery />} />
              <Route path={`bindings/*`} element={<Bindings />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"policy"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail kind={"policy"} itemName={policy.name} resourceId={policy.id} version={policy.version} selfLink={linksOf(policy).self!} refetch={refetch} />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(policy).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
